import React, { useCallback, useRef, useState } from "react";
import { Counterparty } from "@common/types/counterparty";
import {
    CounterpartyAddress,
    CreateCounterpartyAddressBody,
} from "@common/schemas/counterpartyAddress.schema";
import {
    CounterpartyContact,
    CreateCounterpartyContactBody,
} from "@common/schemas/counterpartyContact.schema";
import {
    useCopyGlobalCounterpartyToLocalMutation,
    useCounterpartyDetailsQuery,
    useFetchCounterpartyDetails,
} from "@api/counterparty.api";
import { Loader } from "@design-system/Loader/Loader";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { EditCounterparty } from "../EditCounterparty";
import { CounterpartyAddressesSection } from "./CounterpartyAddressesSection";
import { CounterpartyContactsSection } from "./CounterpartyContactsSection";
import { CounterpartyEditorCommandBar } from "./CounterpartyEditorCommandBar";
import { CounterpartyEditorModalFooter } from "./CounterpartyEditorModalFooter";
import { COUNTERPARTY_EDITOR_COMPANY_FORM_ID } from "./counterpartyEditorFormIds";
import {
    matchClonedAddress,
    matchClonedContact,
} from "./matchClonedCounterpartyChild";
import { useCounterpartyReplacementBooks } from "./useCounterpartyReplacementBooks";
import { useOcrSeedBooks } from "./useOcrSeedBooks";
import styles from "./CounterpartyEditorDrawer.module.scss";

interface Props {
    counterparty: Counterparty;
    companyDirtyRef: React.MutableRefObject<boolean>;
    onEdited: (counterparty: Counterparty) => void;
    onClose: () => void;
    onLocalized: (counterparty: Counterparty) => void;
    onChildEditingChange: (isEditing: boolean) => void;
    formId?: string;
    // "commandbar" (default) renders the drawer's pinned CommandBar and wraps
    // content in the scrolling drawer body, for use inside CounterpartyEditorDrawer.
    // "form" renders an inline sticky footer instead (mirrors
    // CounterpartyEditorDrawerCreate's modal footer) for use inside a Modal, e.g.
    // EditCounterpartyModal.
    footer?: "commandbar" | "form";
    seedContacts?: CreateCounterpartyContactBody[];
    seedAddresses?: CreateCounterpartyAddressBody[];
    seedContactReplacesIds?: ReadonlyArray<string | null>;
    seedAddressReplacesIds?: ReadonlyArray<string | null>;
    removedContactIds?: readonly string[];
    removedAddressIds?: readonly string[];
}

export const CounterpartyEditorDrawerEdit: React.FC<Props> = ({
    counterparty,
    companyDirtyRef,
    onEdited,
    onClose,
    onLocalized,
    onChildEditingChange,
    formId = COUNTERPARTY_EDITOR_COMPANY_FORM_ID,
    footer = "commandbar",
    seedContacts = [],
    seedAddresses = [],
    seedContactReplacesIds = [],
    seedAddressReplacesIds = [],
    removedContactIds = [],
    removedAddressIds = [],
}) => {
    const [isContactEditing, setIsContactEditing] = useState(false);
    const [isAddressEditing, setIsAddressEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { error: showErrorToast } = useToaster();
    const detailsQuery = useCounterpartyDetailsQuery({
        counterpartyId: counterparty.id,
        enabled: true,
    });
    const seeds = useOcrSeedBooks({
        contacts: seedContacts,
        addresses: seedAddresses,
        contactReplacesIds: seedContactReplacesIds,
        addressReplacesIds: seedAddressReplacesIds,
    });
    const copyGlobal = useCopyGlobalCounterpartyToLocalMutation(onLocalized);
    const fetchDetails = useFetchCounterpartyDetails();

    // Dedupe concurrent localizations: without this, two child actions fired
    // before the localized counterparty re-renders (workspaceId still falsy)
    // would each issue a copy request and create duplicate local copies.
    const copyPromiseRef = useRef<Promise<Counterparty> | null>(null);
    const localizeCounterparty = useCallback((): Promise<Counterparty> => {
        if (!copyPromiseRef.current) {
            const promise = copyGlobal.mutateAsync({
                globalCounterpartyId: counterparty.id,
            });
            copyPromiseRef.current = promise;
            // Allow a retry after a failed copy instead of caching the rejection.
            void promise.catch(() => {
                if (copyPromiseRef.current === promise) {
                    copyPromiseRef.current = null;
                }
            });
        }
        return copyPromiseRef.current;
    }, [copyGlobal, counterparty.id]);

    const resolveWritableCounterpartyId = useCallback(async () => {
        if (counterparty.workspaceId) {
            return counterparty.id;
        }
        const local = await localizeCounterparty();
        return local.id;
    }, [counterparty.id, counterparty.workspaceId, localizeCounterparty]);

    // Editing an existing child of a shared counterparty first clones its books
    // to a local copy that re-keys every child, so the original id is stale
    // against the new parent. Resolve the matching clone before mutating it.
    const resolveWritableContact = useCallback(
        async (contact: CounterpartyContact) => {
            if (counterparty.workspaceId) {
                return {
                    counterpartyId: counterparty.id,
                    contactId: contact.id,
                };
            }
            const local = await localizeCounterparty();
            const details = await fetchDetails(local.id);
            const clone = matchClonedContact(contact, details.contacts);
            return {
                counterpartyId: local.id,
                contactId: (clone ?? contact).id,
            };
        },
        [
            counterparty.id,
            counterparty.workspaceId,
            fetchDetails,
            localizeCounterparty,
        ],
    );

    const resolveWritableAddress = useCallback(
        async (address: CounterpartyAddress) => {
            if (counterparty.workspaceId) {
                return {
                    counterpartyId: counterparty.id,
                    addressId: address.id,
                };
            }
            const local = await localizeCounterparty();
            const details = await fetchDetails(local.id);
            const clone = matchClonedAddress(address, details.addresses);
            return {
                counterpartyId: local.id,
                addressId: (clone ?? address).id,
            };
        },
        [
            counterparty.id,
            counterparty.workspaceId,
            fetchDetails,
            localizeCounterparty,
        ],
    );

    const reportChildEditing = ({
        contact,
        address,
    }: {
        contact: boolean;
        address: boolean;
    }) => {
        onChildEditingChange(contact || address);
    };

    const replacementBooks = useCounterpartyReplacementBooks({
        counterpartyId: counterparty.id,
        details: detailsQuery.data,
        contactRows: seeds.contactRows,
        addressRows: seeds.addressRows,
        contactRowReplacesIds: seeds.contactRowReplacesIds,
        addressRowReplacesIds: seeds.addressRowReplacesIds,
        contactReplacements: seeds.contactReplacements,
        addressReplacements: seeds.addressReplacements,
        removedContactIds,
        removedAddressIds,
        fetchDetails,
    });

    const handleEdited = async (edited: Counterparty) => {
        const didReplaceFail = await replacementBooks.saveReplacements(
            edited.id,
        );
        const didSeedSaveFail = await seeds.persist(edited.id);
        if (didReplaceFail || didSeedSaveFail) {
            showErrorToast(
                "Updated counterparty, but couldn't save some extracted contact details",
            );
        }
        onEdited(edited);
    };

    if (detailsQuery.isLoading) {
        return (
            <div className={styles.scrollBody}>
                <Loader />
            </div>
        );
    }

    // Order matches Create everywhere: Basic info, Contacts, Addresses, Tax, Note.
    const contactsAndAddresses = (
        <>
            <CounterpartyContactsSection
                counterpartyId={counterparty.id}
                contacts={replacementBooks.contacts}
                localBook={seeds.contactHandlers}
                suggestedIds={seeds.suggestedContactIds}
                removedIds={replacementBooks.removedContactIds}
                resolveWritableCounterpartyId={resolveWritableCounterpartyId}
                resolveWritableContact={resolveWritableContact}
                onEditingChange={(isEditing) => {
                    setIsContactEditing(isEditing);
                    reportChildEditing({
                        contact: isEditing,
                        address: isAddressEditing,
                    });
                }}
            />
            <CounterpartyAddressesSection
                counterpartyId={counterparty.id}
                addresses={replacementBooks.addresses}
                localBook={seeds.addressHandlers}
                suggestedIds={seeds.suggestedAddressIds}
                removedIds={replacementBooks.removedAddressIds}
                resolveWritableCounterpartyId={resolveWritableCounterpartyId}
                resolveWritableAddress={resolveWritableAddress}
                onEditingChange={(isEditing) => {
                    setIsAddressEditing(isEditing);
                    reportChildEditing({
                        contact: isContactEditing,
                        address: isEditing,
                    });
                }}
            />
        </>
    );

    const form = (
        <EditCounterparty
            key={counterparty.id}
            counterparty={counterparty}
            onEdited={(edited) => {
                void handleEdited(edited);
            }}
            onCancelled={onClose}
            fullWidth
            fields="company"
            dirtyRef={companyDirtyRef}
            onSubmittingChange={setIsSubmitting}
            formId={formId}
            footer="none"
            betweenBasicInfoAndTax={contactsAndAddresses}
        />
    );

    if (footer === "form") {
        return (
            <>
                {form}
                <CounterpartyEditorModalFooter
                    formId={formId}
                    submitLabel="Save"
                    isSubmitting={isSubmitting}
                    onCancel={onClose}
                />
            </>
        );
    }

    return (
        <>
            <div className={styles.scrollBody}>{form}</div>
            <CounterpartyEditorCommandBar
                submitLabel="Save"
                formId={formId}
                onCancel={onClose}
            />
        </>
    );
};
